import { DEFAULT_FLOOR_PLAN } from '../../utils/floorPlanGeometry.logic';
import React from 'react';
import { Group, Line, Text } from 'react-konva';
import { useEditorStore, PPM, CELL } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';

const MINOR_COLOR = '#1a2030';
const MAJOR_COLOR = '#222d40';
const MAJOR_EVERY = 5; // every 5 cells = major line

export const GridLayer: React.FC = () => {
  const { stageX, stageY, stageScale, stageW, stageH, floorPlan } = useEditorStore(useShallow(state => ({
    stageX: state.stageX, stageY: state.stageY, stageScale: state.stageScale, stageW: state.stageW, stageH: state.stageH,
    floorPlan: state.dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN,
  })));

  // Don't render sub-pixel lines — LOD: hide minor grid when zoomed way out
  const showMinor = stageScale > 0.25;
  const showLabels = stageScale > 0.6;

  const gridPx = floorPlan.tileSizeMm / 1000 * PPM;
  const originX = floorPlan.originXMm / 1000 * PPM;
  const originY = floorPlan.originYMm / 1000 * PPM;
  const startX = originX + Math.floor((-stageX / stageScale - originX) / gridPx) * gridPx;
  const endX   = startX + stageW / stageScale + gridPx * 2;
  const startY = originY + Math.floor((-stageY / stageScale - originY) / gridPx) * gridPx;
  const endY   = startY + stageH / stageScale + gridPx * 2;

  const lines: React.ReactNode[] = [];

  for (let x = startX; x <= endX; x += CELL) {
    const isMajor = Math.abs(Math.round((x - originX) / gridPx)) % MAJOR_EVERY === 0;
    if (!showMinor && !isMajor) continue;
    lines.push(
      <Line
        key={`vx${x}`}
        points={[x, startY, x, endY]}
        stroke={isMajor ? MAJOR_COLOR : MINOR_COLOR}
        strokeWidth={(isMajor ? 2 : 1) / stageScale}
        listening={false}
      />
    );
    if (showLabels && isMajor) {
      lines.push(
      <Text
          key={`lx${x}`}
          x={x + 3 / stageScale}
          y={startY + 3 / stageScale}
          text={`${((x - originX) / PPM + floorPlan.originXMm / 1000).toFixed(1)}m`}
          fontSize={10 / stageScale}
          fill="#3a4a5a"
          listening={false}
        />
      );
    }
  }

  for (let y = startY; y <= endY; y += CELL) {
    const isMajor = Math.abs(Math.round((y - originY) / gridPx)) % MAJOR_EVERY === 0;
    if (!showMinor && !isMajor) continue;
    lines.push(
      <Line
        key={`vy${y}`}
        points={[startX, y, endX, y]}
        stroke={isMajor ? MAJOR_COLOR : MINOR_COLOR}
        strokeWidth={(isMajor ? 2 : 1) / stageScale}
        listening={false}
      />
    );
  }

  return <Group listening={false}>{lines}</Group>;
};
